import { memo, useCallback, useContext, useMemo } from 'react';

import { Grid } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Link } from 'react-router-dom';
import reduce from 'lodash/reduce';
import startCase from 'lodash/startCase';
import lowerCase from 'lodash/lowerCase';

import { useRouter } from '../../hooks/useRouter';
import { Context as RolesContext } from '../../api/roles/context';
import {
    ALWAYS_SKIP_SEGMENTS,
    CRM_MERGED_FROM_GENERAL,
    PATH_SEGMENT_LABELS
} from './constant';
import AddToFavoritesButton from './AddToFavoritesButton';

const BreadcrumbsByLocation = () => {
    const { queryObject, location } = useRouter();
    const rolesContext = useContext(RolesContext);

    const breadcrumbs = useMemo(() => {
        const isHomePage = location.pathname === '/'; /* || location.pathname === '/operations' */
        if (isHomePage) return [{ title: 'Home', path: '' }];

        return reduce(
            location.pathname.split('/'),
            (acc, next, index, segments) => {
                if (next) {
                    const fullPath = `/${segments.slice(1, index + 1).join('/')}`;

                    // Operations is exposed as its own navigation branch even though
                    // its routes live under /administration/operations.
                    if (next === 'administration' && segments[index + 1] === 'operations') {
                        return acc;
                    }

                    // Structural segments with no matching sidebar node usually don't get crumbs.
                    // Keep "setup" under Administration as Master Data, except for Admin subtree
                    // where UX expects: Administration -> Users & Roles -> ...
                    const keepSetupCrumbUnderAdministration =
                        next === 'setup' &&
                        segments[index - 1] === 'administration' &&
                        segments[index + 1] !== 'admin' &&
                        !(segments[index + 1] === 'general' && segments[index + 2] === 'settings');

                    if (
                        ALWAYS_SKIP_SEGMENTS.includes(next) &&
                        !keepSetupCrumbUnderAdministration
                    ) {
                        return acc;
                    }

                    // "general" is shared by three sidebar sub-categories depending on what follows it:
                    // Settings (skip — "settings" renders its own crumb next), CRM (items merged from
                    // the old "General" setup column), or Geography (everything else under Master Data).
                    if (next === 'general') {
                        const following = segments[index + 1];
                        if (following === 'settings') return acc;
                        const isCrmSubSection = CRM_MERGED_FROM_GENERAL.includes(following);
                        acc.push({
                            title: isCrmSubSection ? 'CRM' : 'Geography',
                            path: isCrmSubSection ? '/administration/setup/crm' : fullPath
                        });
                        return acc;
                    }

                    // Finance's "documents" grouping segment has no page of its own for the Issue/Find
                    // leaves — fold it away so "Issue Documents"/"Find Documents" render as one crumb.
                    if (next === 'documents' && ['issue', 'find'].includes(segments[index + 1])) {
                        return acc;
                    }

                    acc.push({
                        title: PATH_SEGMENT_LABELS?.[next] || startCase(lowerCase(next)),
                        path: fullPath
                    });

                    if (next === 'travel-file') {
                        acc[acc.length - 1].title += ` (${queryObject.refNumber || ''})`;
                    }

                    // Replace numeric role IDs with the role's display name
                    // (path: /administration/setup/admin/roles/<id>)
                    if (/^\d+$/.test(next) && acc.length >= 2 && acc[acc.length - 2].title === 'Roles') {
                        const role = (rolesContext?.data || []).find((r: any) => String(r._id) === next);
                        if (role?.type) {
                            acc[acc.length - 1].title = role.type;
                        }
                    }
                    // For new role creation: /administration/setup/admin/roles/new
                    if (next === 'new' && acc.length >= 2 && acc[acc.length - 2].title === 'Roles') {
                        acc[acc.length - 1].title = 'New Role';
                    }

                    if (acc[acc.length - 1].path === '/reservations/tour/book-tour') {
                        acc[acc.length - 2]['saveSearch'] = true;
                    }

                    if (acc[acc.length - 1].path.includes('travel-file-list/travel-file')) {
                        acc[acc.length - 1]['saveSearch'] = true;
                    }
                }
                return acc;
            },
            [] as Array<{ title: string; path: string; saveSearch?: boolean }>
        );
    }, [location.pathname, queryObject.refNumber, rolesContext?.data]);

    // The handler now receives the resolved target URL too. Reload only when
    // the target matches the current URL exactly — otherwise we'd fire a
    // page reload at the same time the Link tries to navigate, blocking the
    // navigation. When the click actually changes the URL (e.g. dropping
    // serviceId/serviceType from the Travel File breadcrumb), let React
    // Router's <Link> handle it without reload.
    const handleClick = useCallback(
        (last: boolean, target: { pathname: string; search: string }) => () => {
            if (!last) return;
            const sameUrl =
                target.pathname === location.pathname && (target.search || '') === (location.search || '');
            if (sameUrl) window.location.reload(/* false */);
        },
        [location.pathname, location.search]
    );

    return (
        <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                px: 2.5,
                py: 0.75,
                minHeight: 40,
                '@media (max-width: 600px)': { display: 'none' },
                '@media (max-height: 500px)': { display: 'none' }
            }}
        >
            <Breadcrumbs
                sx={{
                    '& .MuiBreadcrumbs-separator': { mx: 0.5, color: 'text.secondary' },
                    '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' }
                }}
                separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
                aria-label="breadcrumb"
            >
                {breadcrumbs.map(({ title, path, saveSearch }, idx, list) => {
                    const last = idx === list.length - 1;
                    const toPathname = last ? location.pathname : path;
                    let toSearch = saveSearch ? location.search : '';
                    // For the Travel File breadcrumb, drop inline-expand params
                    // when present so a click navigates from the inline form
                    // to TF home (sections view) instead of just reloading
                    // the same inline view.
                    if (last && toPathname.includes('travel-file-list/travel-file')) {
                        const params = new URLSearchParams(location.search);
                        let dirty = false;
                        for (const k of ['serviceId', 'serviceType', 'serviceTab', 'tab', 'propValue']) {
                            if (params.has(k)) {
                                params.delete(k);
                                dirty = true;
                            }
                        }
                        if (dirty) toSearch = params.toString() ? `?${params.toString()}` : '';
                    }
                    const target = { pathname: toPathname, search: toSearch };
                    const displayTitle = title === 'Find By Pnr' ? 'Find By PNR' : title;
                    return (
                        <Link
                            style={{
                                padding: '4px 8px',
                                borderRadius: 6,
                                fontSize: '0.8125rem',
                                fontWeight: 400,
                                color: '#6b7280',
                                textDecoration: 'none',
                                transition: 'background-color 0.15s',
                                letterSpacing: '0.01em'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            to={target}
                            key={path}
                            onClick={handleClick(last, target)}
                            // Accessibility: Add aria-current="page" only to the active (last) breadcrumb item so assistive technologies can identify the current page.
                            {...(last ? { 'aria-current': 'page' } : {})}
                        >
                            {displayTitle}
                        </Link>
                    );
                })}
            </Breadcrumbs>
            <AddToFavoritesButton />
        </Grid>
    );
};

export default memo(BreadcrumbsByLocation);
